import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Add, Search } from '@mui/icons-material';
import { Box, Typography, Tooltip, IconButton, Avatar } from '@mui/material';
import { useStyles } from '../styles/sectionStyles';
import { isAdmin, isClient, isManager, isStakeholder } from '../../../utils/share';
import { PROJECT_EDITOR_ROLE } from '../../../Constants/ProjectConstants';
import BoardSearchField from '../../../components/board-search';

const SectionHeader = ({
  section,
  taskCount,
  onAddClick,
  sectionQuery,
  onSectionQueryChange,
  visibleCount,
  totalCount,
  isFiltering,
}) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [showSearch, setShowSearch] = useState(false);

  const handleToggleSearch = useCallback(() => {
    setShowSearch((prev) => {
      // Closing the field drops its query, so a column is never left silently filtered.
      if (prev) onSectionQueryChange('');
      return !prev;
    });
  }, [onSectionQueryChange]);
  const accountData = useSelector((state) => state?.login?.user);

  const goToProjects = () => {
    navigate(`/project/board/${section?.id}`);
  };

  const handleAddTaskClick = () => {
    onAddClick(section);
  };

  let projectmember = section?.projectmember;
  if (Array.isArray(projectmember)) {
    projectmember = projectmember.find((data) => data.userId === accountData?.id);
  }

  const projectRole = projectmember?.projectRoles?.roleName ?? '';

  const isEditPermission = isAdmin() || isManager() || PROJECT_EDITOR_ROLE.includes(projectRole);

  function stringAvatar(name) {
    if (!name) return {};
    return {
      sx: {
        textAlign: 'center',
        bgcolor: 'inherit',
        border: '1px solid #000',
        // BorderColor: stringToColor(name),
        color: '#000',
      },
      children: `${name}`,
    };
  }
  const createTaskDisabled = !section?.allowManualTask && !isEditPermission;
  return (
    <>
      <header className={classes.header}>
      <Box className={classes.titleBox}>
        <Tooltip title={section?.projectName} placement="top" arrow>
          <Typography variant="h6" noWrap className={classes?.projectName} onClick={goToProjects}>
            {section?.projectName}
          </Typography>
        </Tooltip>

        <Tooltip
          title={
            isFiltering
              ? `${visibleCount} of ${totalCount} task${totalCount === 1 ? '' : 's'} match`
              : `${taskCount} task${taskCount === 1 ? '' : 's'} in this section`
          }
          placement="top"
          arrow
        >
          <Avatar
            {...stringAvatar(isFiltering ? `${visibleCount}/${totalCount}` : taskCount.toString())}
            className={classes?.avatar}
          />
        </Tooltip>
      </Box>
      <Box className={classes.actionBox}>
        <Tooltip title="Search tasks in this project" placement="top" arrow>
          <IconButton size="small" onClick={handleToggleSearch}>
            <Search fontSize="small" />
          </IconButton>
        </Tooltip>
        {(isEditPermission || isAdmin() || section?.allowManualTask || isStakeholder() || isClient()) && (
          <Tooltip title={isStakeholder() ? 'Add Issue' : 'Add Task'} placement="top" arrow>
            <IconButton size="small" onClick={handleAddTaskClick}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      </header>
      {showSearch && (
        <BoardSearchField
          compact
          autoFocus
          value={sectionQuery}
          onChange={onSectionQueryChange}
          onClose={handleToggleSearch}
          placeholder="Search in this project"
        />
      )}
    </>
  );
};

SectionHeader.propTypes = {
  section: PropTypes.object,
  onAddClick: PropTypes.func,
  taskCount: PropTypes.number,
  sectionQuery: PropTypes.string,
  onSectionQueryChange: PropTypes.func,
  visibleCount: PropTypes.number,
  totalCount: PropTypes.number,
  isFiltering: PropTypes.bool,
};

export default SectionHeader;
